'use client';

import type { ReactNode } from 'react';

import Link from 'next/link';

import { cn } from '../lib/utils';

/**
 * Segment — eckiger Umschalter mit Zähler (subcontracts `.seg`, #1329).
 *
 * Für Zustand auf derselben Seite (Filter „Offen / Alle", Ansicht „Tag ·
 * Woche · Monat"). Wer eine andere Route meint, nimmt `Reiter`.
 *
 * Steht der Zustand in der ADRESSE (`?ansicht=woche`), trägt jede Option ihr
 * `href`: dann ist sie ein Link mit `aria-current`, und ein Server-Bauteil
 * kann den Umschalter mit Daten füllen, ohne einen Rückruf zu übergeben.
 */
export type SegmentOption<K extends string> = {
  key: K;
  label: ReactNode;
  /** Steht in Klammern hinter der Beschriftung — „Offen (22)". */
  zaehler?: number;
  dataTest?: string;
  /** Nicht wählbar (grau über Farbe, nie `opacity`, Regel 532). */
  gesperrt?: boolean;
  /** Zustand in der Adresse: die Option ist ein Link (ohne Sprung nach oben). */
  href?: string;
};

export function Segment<K extends string>({
  optionen,
  wert,
  onWechsel,
  label,
  beschriftetVon,
  dataTest,
  umbrechen = true,
}: {
  optionen: SegmentOption<K>[];
  wert: K;
  /** Pflicht, solange eine Option kein `href` trägt. */
  onWechsel?: (key: K) => void;
  /** Name der Gruppe — oder `beschriftetVon`, wenn ein sichtbares Label ihn trägt. */
  label?: string;
  /** `id` eines sichtbaren Labels (aria-labelledby) statt eines zweiten Namens. */
  beschriftetVon?: string;
  /** Filterreihen enden auf `-filter` — Smoke S10 misst dort „eckig". */
  dataTest?: string;
  /**
   * Ab 768 px darf ein Filter-Segment umbrechen (subcontracts `.seg`). Ein
   * Symbol-Umschalter (`AnsichtsWahl`) bricht NIE um — zwei Symbole
   * untereinander lesen sich nicht als Wahl (Feinschliff 06.10.2026,
   * Vermietungen 1440).
   *
   * Am Telefon (< 768 px) bricht KEIN Segment um, unabhängig von diesem Prop
   * (Sichtprüfung 07.10.2026, Vermietungen 390 px: „Abgeschlossen" stand allein
   * in einer zweiten Zeile). Die Wanne passt erst ihre Schrift an (539: eine
   * Stufe, 14 → 12 px, und engeres Polster), und reicht das nicht, rollt sie
   * waagerecht in sich (541) — keine Option wird beschnitten oder versteckt,
   * die Seite rollt nicht seitwärts (S9a).
   */
  umbrechen?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={beschriftetVon ? undefined : label}
      aria-labelledby={beschriftetVon}
      data-test={dataTest}
      className={cn(
        // Entscheid B (06.10.2026): 4 px an der Gruppe; die Optionen runden nur
        // ihre Aussenkanten — `overflow-hidden` schnitte den Fokusring ab.
        'border-border bg-card shadow-segment rounded-control inline-flex border',
        '[&>*:first-child]:rounded-l-control [&>*:last-child]:rounded-r-control',
        // Telefon: eine Zeile, rollt in sich (Leiste ausgeblendet wie `Reiter`).
        'max-w-full flex-nowrap overflow-x-auto [scrollbar-width:none] md:overflow-visible [&::-webkit-scrollbar]:hidden',
        umbrechen && 'md:flex-wrap',
      )}
    >
      {optionen.map((option) => {
        const istAktiv = option.key === wert;
        const klassen = cn(
          // Telefon: eine Schriftstufe kleiner und engeres Polster (539), die
          // Option schrumpft nie und bricht ihr Wort nicht.
          'inline-flex min-h-11 shrink-0 items-center gap-1 px-2 py-1.5 text-xs whitespace-nowrap md:min-h-0 md:px-3 md:text-sm',
          // Fokus: der globale 2-px-Umriss (primary) — `relative`/`z-10`, damit der
          // Nachbar ihn nicht verdeckt. Am Telefon schneidet die rollende Wanne
          // einen Umriss AUSSERHALB ab; dort liegt er innen, und auf dem gefüllten
          // Aktiven in der Vordergrundfarbe (ein Indigo-Innenring verschwände dort).
          'relative focus-visible:z-10 max-md:focus-visible:-outline-offset-2',
          istAktiv
            ? // #1329 (Auftraggeber 05.10.2026): aktiv in Indigo statt fast schwarz —
              // subcontracts füllt mit seiner dunklen Markenfarbe, die bei uns als Schwarz las.
              'bg-primary text-primary-foreground max-md:focus-visible:outline-primary-foreground'
            : 'bg-card text-muted-foreground hover:bg-tint hover:text-primary disabled:text-disabled-foreground disabled:hover:bg-card disabled:cursor-not-allowed',
        );
        const inhalt = (
          <>
            {option.label}
            {option.zaehler !== undefined ? (
              <span className="tabular-nums">{` (${option.zaehler})`}</span>
            ) : null}
          </>
        );

        return option.href ? (
          <Link
            key={option.key}
            href={option.href}
            scroll={false}
            aria-current={istAktiv ? 'page' : undefined}
            data-test={option.dataTest}
            className={klassen}
          >
            {inhalt}
          </Link>
        ) : (
          <button
            key={option.key}
            type="button"
            aria-pressed={istAktiv}
            data-test={option.dataTest}
            // Die gewählte Option ist nie gesperrt: ein Klick darauf ändert
            // nichts, und gesperrt trug sie `text-disabled-foreground` auf
            // Indigo — dunkle Schrift auf der Fläche (gemessen 05.10.2026, T19b).
            disabled={option.gesperrt && !istAktiv}
            onClick={() => onWechsel?.(option.key)}
            className={klassen}
          >
            {inhalt}
          </button>
        );
      })}
    </div>
  );
}
